import { Link, useRouterState } from "@tanstack/react-router";
import { Fragment, useEffect, useState } from "react";
import { User, Menu, X, ChevronDown } from "lucide-react";
import { site } from "@/lib/site";
import { SiteLink } from "@/components/site/SiteLink";
import { supabase } from "@/integrations/supabase/client";
import logo from "@/assets/logo.webp";
import { hasAdminAccess } from "@/lib/admin-access";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";


const resourcesLinks = [
  {
    to: "/resources",
    label: "F1 Visa Guides & Resources",
    description: "Every guide in one library",
  },
  {
    to: "/us-visa-scheduling-guide",
    label: "US Visa Scheduling Guide",
    description: "ustraveldocs portal walkthrough",
  },
  {
    to: "/f1-visa-interview-questions",
    label: "F1 Interview Questions",
    description: "Real questions with model answers",
  },
  {
    to: "/refusal-decoder",
    label: "214(b) Refusal Decoder",
    description: "Find why you were refused",
  },
  {
    to: "/resources/214b-vs-221g-difference",
    label: "214(b) vs 221(g)",
    description: "What each outcome really means",
  },
  {
    to: "/refusal-case-study",
    label: "Refusal Case Study",
    description: "A real re-attempt, start to finish",
  },
  {
    to: "/find-your-plan",
    label: "Find Your Plan",
    description: "60-second plan finder quiz",
  },
] as const;


const primaryLinks = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/pricing", label: "Pricing" },
  { to: "/achievements", label: "Wins" },
  { to: "/contact", label: "Contact" },
] as const;

const legalLinks = [
  { to: "/about", label: "About us" },
  { to: "/terms", label: "Terms & Conditions" },
  { to: "/privacy", label: "Privacy Policy" },
  { to: "/refund", label: "Refund Policy" },
] as const;

// Desktop nav still shows the full primary set (Wins + About visible on lg+).
const desktopLinks = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/pricing", label: "Pricing" },
  { to: "/achievements", label: "Wins" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
] as const;

export function Header() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [signedIn, setSignedIn] = useState(false);
  const [admin, setAdmin] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data }) => {
      setSignedIn(!!data.session);
      setAdmin(data.session ? await hasAdminAccess(data.session) : false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      setSignedIn(!!session);
      if (!session) setAdmin(false);
      else void hasAdminAccess(session).then(setAdmin);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  // Close the mobile drawer whenever the route changes.
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  const isActive = (to: string) => (to === "/" ? pathname === "/" : pathname.startsWith(to));

  return (
    <header className="sticky top-0 z-50 w-full overflow-x-hidden backdrop-blur-md bg-background/85 border-b border-border/60">
      <div className="mx-auto w-full max-w-7xl px-3 sm:px-5 lg:px-8 h-16 flex items-center justify-between gap-2 sm:gap-3">
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <img
            src={logo}
            alt="WeLivdIt - US Visa Interview Coaching"
            width={40}
            height={40}
            decoding="async"
            fetchPriority="high"
            className="block h-7 w-7 sm:h-8 sm:w-8 lg:h-9 lg:w-9 object-contain shrink-0"
          />
          <span className="font-display text-base sm:text-lg font-semibold tracking-tight">
            We<span className="text-primary">Livd</span>It
          </span>
        </Link>

        {/* Desktop nav (md+) */}
        <nav className="hidden md:flex flex-1 min-w-0 overflow-x-auto no-scrollbar">
          <ul className="flex items-center justify-center gap-0.5 mx-auto">
            {desktopLinks.map((l) => (
              <Fragment key={l.to}>
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className={
                      "inline-block px-2 lg:px-2.5 py-1.5 rounded-full text-sm transition whitespace-nowrap " +
                      (isActive(l.to)
                        ? "text-foreground font-semibold bg-secondary"
                        : "text-foreground/70 hover:text-foreground hover:bg-secondary/60")
                    }
                  >
                    {l.label}
                  </Link>
                </li>
                {l.label === "Wins" && (
                  <li key="resources-dd">
                    <DropdownMenu>
                      <DropdownMenuTrigger
                        className={
                          "inline-flex items-center gap-1 px-2 lg:px-2.5 py-1.5 rounded-full text-sm transition whitespace-nowrap outline-none focus-visible:ring-2 focus-visible:ring-ring " +
                          (resourcesLinks.some((r) => isActive(r.to))
                            ? "text-foreground font-semibold bg-secondary"
                            : "text-foreground/70 hover:text-foreground hover:bg-secondary/60")
                        }
                      >
                        Resources
                        <ChevronDown className="h-3.5 w-3.5 opacity-70" />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="center" sideOffset={8} className="w-72">
                        {resourcesLinks.map((r) => (
                          <DropdownMenuItem key={r.to} asChild>
                            <SiteLink path={r.to} className="flex flex-col items-start gap-0.5 cursor-pointer">
                              <span className="font-medium text-sm">{r.label}</span>
                              <span className="text-xs text-muted-foreground">{r.description}</span>
                            </SiteLink>
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </li>
                )}
              </Fragment>
            ))}
          </ul>
        </nav>

        {/* Desktop right side */}
        <div className="hidden md:flex items-center gap-2 shrink-0">
          {signedIn ? (
            <Link
              to={admin ? "/admin" : "/student-dashboard"}
              className="relative inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-full bg-gradient-cta text-primary-foreground shadow-md hover:opacity-90 transition ring-2 ring-primary/30 animate-pulse-slow"
              aria-label={admin ? "Admin dashboard" : "My dashboard"}
            >
              <User className="h-4 w-4" />
              <span>{admin ? "Admin" : "Dashboard"}</span>
              <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-emerald-400 ring-2 ring-background" />
            </Link>
          ) : (
            <Link
              to="/auth"
              className="inline-flex items-center px-3 lg:px-4 py-2 rounded-full border border-border bg-background text-sm font-medium hover:bg-secondary transition"
            >
              Sign in
            </Link>
          )}
          <Link
            to="/find-your-plan"
            className="hidden lg:inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-primary/40 bg-primary/5 text-primary text-sm font-semibold hover:bg-primary/10 transition whitespace-nowrap"
          >
            Find Your Plan
          </Link>
          <Link
            to="/pricing"
            className="inline-flex items-center gap-1.5 px-4 lg:px-5 py-2 rounded-full bg-gradient-cta text-primary-foreground text-sm font-semibold shadow-glow hover:opacity-90 transition whitespace-nowrap"
          >
            Book a Session
          </Link>
        </div>

        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <button
              type="button"
              aria-label="Open menu"
              className="md:hidden inline-flex items-center justify-center h-10 w-10 rounded-md text-foreground/80 hover:bg-secondary"
            >
              {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </SheetTrigger>
          <SheetContent
            side="top"
            className="flex h-[100dvh] max-h-screen w-full max-w-full flex-col overflow-hidden border-0 bg-background p-0"
          >
            <div className="flex shrink-0 items-center justify-between px-4 h-16 border-b border-border/60">
              <Link to="/" className="flex items-center gap-2" onClick={() => setMobileOpen(false)}>
                <img src={logo} alt="WeLivdIt logo - US Visa Interview Coaching" width={40} height={40} className="block h-8 w-8 object-contain shrink-0" />
                <span className="font-display text-lg font-semibold">
                  We<span className="text-primary">Livd</span>It
                </span>
              </Link>
              <button
                type="button"
                aria-label="Close menu"
                onClick={() => setMobileOpen(false)}
                className="inline-flex h-10 w-10 items-center justify-center rounded-md text-foreground/80 hover:bg-secondary"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto overscroll-contain px-4 py-5 pb-8">
              {primaryLinks.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={() => setMobileOpen(false)}
                  className={
                    "block rounded-xl px-4 py-3 text-base transition " +
                    (isActive(l.to)
                      ? "bg-secondary font-semibold text-foreground"
                      : "text-foreground/85 hover:bg-secondary/60")
                  }
                >
                  {l.label}
                </Link>
              ))}

              <div className="mt-5 pt-4 border-t border-border/60">
                <p className="px-4 text-[11px] font-semibold tracking-wider text-muted-foreground uppercase mb-1">
                  Resources
                </p>
                {resourcesLinks.map((r) => (
                  <SiteLink
                    key={r.to}
                    path={r.to}
                    onClick={() => setMobileOpen(false)}
                    className="block rounded-xl px-4 py-2.5 text-base text-foreground/85 hover:bg-secondary/60"
                  >
                    {r.label}
                  </SiteLink>
                ))}
              </div>


              <div className="mt-5 pt-4 border-t border-border/60">
                <p className="px-4 text-[11px] font-semibold tracking-wider text-muted-foreground uppercase mb-1">
                  Legal
                </p>
                {legalLinks.map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    onClick={() => setMobileOpen(false)}
                    className="block rounded-xl px-4 py-2.5 text-base text-foreground/80 hover:bg-secondary/60"
                  >
                    {l.label}
                  </Link>
                ))}
              </div>

              <div className="mt-6 space-y-3 px-1">
                {signedIn ? (
                  <Link
                    to={admin ? "/admin" : "/student-dashboard"}
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-full border border-border text-sm font-medium hover:bg-secondary"
                  >
                    <User className="h-4 w-4" />
                    {admin ? "Admin" : "Dashboard"}
                  </Link>
                ) : (
                  <Link
                    to="/auth"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-full border border-border text-sm font-medium hover:bg-secondary"
                  >
                    <User className="h-4 w-4" /> Sign in
                  </Link>
                )}
                <a
                  href={site.whatsapp}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center justify-center w-full px-4 py-3 rounded-full bg-gradient-cta text-primary-foreground text-sm font-semibold shadow-soft"
                >
                  Talk to us
                </a>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
